import { UserService } from "../../services/user.service.js";
import { PointService } from "../../services/point.service.js";
import { ModalManager } from "../../utils/ModalManager.js";
import { parseMarkdown } from "../../utils/markdown.js";

/**
 * Standard Purchase Modal (Cosmic Glass Style)
 * Supports: Rent vs Own, Dynamic Pricing, Adult Surcharge
 */
export class PurchaseModal {
    /**
     * @param {object} props
     * @param {string} props.title - Modal Title (e.g. "Unlock Episode")
     * @param {string} props.itemName - Episode Title
     * @param {number} props.rentPrice - Rental Cost (Activity/Star)
     * @param {number} props.rentDays - Rental Period (Default: 3)
     * @param {number} props.ownPrice - Ownership Cost (Star Only usually, but configurable)
     * @param {boolean} props.isAdult - If true, displays 19+ badge and implies surcharge included in prices
     * @param {function} props.onConfirm - Callback(type) => Promise<void>. type: 'rent' | 'own'
     */
    constructor({ title, itemName, rentPrice, rentDays, ownPrice, isAdult, onConfirm }) {
        this.title = title || "Unlock Content";
        this.itemName = itemName || "Episode";

        // Prices should ALREADY include surcharge from caller, but we display badge if isAdult
        this.rentPrice = rentPrice || 0;
        this.rentDays = rentDays || 7; // Default increased to 7 per user request
        this.ownPrice = ownPrice || 0;
        this.isAdult = !!isAdult;

        this.onConfirm = onConfirm;
        this.dom = null;
        this.selectedOption = 'rent'; // 'rent' | 'own'
    }

    render() {
        // 1. Overlay (Standard ID for programmatic control if needed)
        const overlay = document.createElement('div');
        overlay.id = 'purchase-modal-overlay';
        overlay.className = "fixed inset-0 z-[10000] flex items-center justify-center p-4";
        // Deep Space Gradient Background
        overlay.style.background = "radial-gradient(circle at center, rgba(20, 10, 40, 0.85) 0%, rgba(5, 5, 10, 0.98) 100%)";
        overlay.style.backdropFilter = "blur(12px)";
        overlay.style.opacity = "0"; // For animation
        overlay.style.transition = "opacity 0.3s ease-out";

        // 2. Modal Card (Cosmic Glass Panel)
        const card = document.createElement('div');
        card.className = "w-full max-w-sm glass-panel rounded-3xl overflow-hidden shadow-2xl relative border border-white/10 transform scale-95 transition-all duration-300";
        card.style.background = "rgba(15, 23, 42, 0.6)"; // Fallback
        card.style.backdropFilter = "blur(20px)";
        card.style.boxShadow = "0 0 40px rgba(139, 92, 246, 0.15), inset 0 0 0 1px rgba(255, 255, 255, 0.1)";

        // Inner HTML
        card.innerHTML = `
            <!-- Header -->
            <div class="px-6 py-5 border-b border-white/5 relative overflow-hidden bg-gradient-to-r from-violet-500/10 to-transparent">
                <div class="flex items-center justify-between relative z-10">
                    <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
                            <span class="material-symbols-outlined text-white text-xl">lock_open</span>
                        </div>
                        <div>
                            <h3 class="font-bold text-white text-lg tracking-wide">${this.title}</h3>
                            <p class="text-[10px] text-zinc-400 font-mono tracking-widest uppercase">PREMIUM CONTENT</p>
                        </div>
                    </div>
                    <button id="btn-close" class="text-white/40 hover:text-white transition-colors">
                        <span class="material-symbols-outlined">close</span>
                    </button>
                </div>
                ${this.isAdult ? `
                    <div class="absolute top-0 right-0 px-2 py-1 bg-red-600/20 border-l border-b border-red-500/30 rounded-bl-xl">
                        <span class="text-[10px] font-black text-red-500 flex items-center gap-1">
                            <span class="material-symbols-outlined text-[12px]">18_up_rating</span> 19+
                        </span>
                    </div>
                ` : ''}
            </div>

            <!-- Body -->
            <div class="p-6 space-y-6">
                <!-- Item Info -->
                <div class="text-center">
                    <p class="text-violet-200/60 text-xs mb-2">You are unlocking</p>
                    <h4 class="text-white font-bold text-xl leading-tight line-clamp-2 drop-shadow-md">
                        ${this.itemName}
                    </h4>
                </div>

                <!-- Options Grid -->
                <div class="grid grid-cols-2 gap-3" id="option-grid">
                    <!-- Option: Rent -->
                    <label class="cursor-pointer relative group">
                        <input type="radio" name="purchase_type" value="rent" checked class="peer sr-only">
                        <div class="p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-violet-500/50 peer-checked:bg-violet-600/20 peer-checked:border-violet-500 peer-checked:shadow-[0_0_20px_rgba(139,92,246,0.2)] transition-all h-full flex flex-col items-center justify-center gap-2">
                            <span class="text-zinc-400 text-xs font-bold uppercase tracking-wider peer-checked:text-violet-300">Rent</span>
                            <div class="flex items-center gap-1">
                                <span class="text-2xl font-black text-white">${this.rentPrice}</span>
                                <span class="text-[10px] text-zinc-500 font-bold mt-2">P</span>
                            </div>
                            <span class="text-[10px] text-zinc-500">${this.rentDays} Days</span>
                        </div>
                    </label>

                    <!-- Option: Own -->
                    <label class="cursor-pointer relative group">
                        <input type="radio" name="purchase_type" value="own" class="peer sr-only">
                        <div class="p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-fuchsia-500/50 peer-checked:bg-fuchsia-600/20 peer-checked:border-fuchsia-500 peer-checked:shadow-[0_0_20px_rgba(232,121,249,0.2)] transition-all h-full flex flex-col items-center justify-center gap-2">
                            <span class="text-zinc-400 text-xs font-bold uppercase tracking-wider peer-checked:text-fuchsia-300">Own</span>
                            <div class="flex items-center gap-1">
                                <span class="text-2xl font-black text-white">${this.ownPrice}</span>
                                <span class="text-[10px] text-zinc-500 font-bold mt-2">Star</span>
                            </div>
                            <span class="text-[10px] text-zinc-500">Forever</span>
                        </div>
                    </label>
                </div>

                <!-- Balance Status -->
                <div id="balance-status" class="bg-black/20 rounded-xl p-3 border border-white/5">
                    <div class="flex items-center justify-between text-xs text-zinc-400 mb-1">
                        <span>Current Balance</span>
                        <button class="text-violet-400 hover:text-violet-300 underline" id="btn-charge">Charge</button>
                    </div>
                    <div class="flex items-center justify-between font-mono text-sm">
                        <div class="flex items-center gap-2">
                            <span class="w-2 h-2 rounded-full bg-violet-500"></span>
                            <span class="text-white" id="user-points">Loading...</span>
                        </div>
                        <div class="flex items-center gap-2">
                            <span class="w-2 h-2 rounded-full bg-amber-400"></span>
                            <span class="text-white" id="user-stars">Loading...</span>
                        </div>
                    </div>
                </div>

                <!-- Surcharge Note (if Adult) -->
                ${this.isAdult ? `
                    <p class="text-center text-[10px] text-red-400/80 bg-red-900/10 py-1 rounded border border-red-500/10">
                        * Contains 19+ Surcharge (+50P)
                    </p>
                ` : ''}
            </div>

            <!-- Footer -->
            <div class="p-6 border-t border-white/5 grid grid-cols-2 gap-3">
                <button id="btn-cancel" class="px-4 py-3 rounded-xl font-bold text-zinc-400 hover:text-white hover:bg-white/5 transition-colors">
                    Cancel
                </button>
                <button id="btn-confirm" disabled class="px-4 py-3 rounded-xl font-bold text-white bg-white/10 cursor-not-allowed flex items-center justify-center gap-2 relative overflow-hidden transition-all">
                    <span class="material-symbols-outlined text-sm animate-spin hidden" id="spinner">progress_activity</span>
                    <span id="btn-text" class="relative z-10 transition-colors">Confirm</span>
                    <div class="absolute inset-0 bg-gradient-to-r from-violet-600 to-fuchsia-600 opacity-0 transition-opacity" id="btn-glow"></div>
                </button>
            </div>
        `;

        overlay.appendChild(card);
        document.body.appendChild(overlay);
        this.dom = overlay;

        // Animations
        requestAnimationFrame(() => {
            overlay.style.opacity = "1";
            card.classList.remove('scale-95');
            card.classList.add('scale-100');
        });

        // Event Binding
        this.bindEvents(overlay);
        this.initBalanceCheck();
    }

    bindEvents(overlay) {
        // Close / Cancel
        const close = () => this.destroy();
        overlay.querySelector('#btn-close').onclick = close;
        overlay.querySelector('#btn-cancel').onclick = close;

        // Option Change
        const radios = overlay.querySelectorAll('input[name="purchase_type"]');
        radios.forEach(r => {
            r.onchange = (e) => {
                this.selectedOption = e.target.value;
                this.checkAffordability(); // Re-check logic
            };
        });

        // Charge
        overlay.querySelector('#btn-charge').onclick = () => {
            if (confirm("Go to Charging Station?")) {
                this.destroy();
                window.router.navigate('/charging-station'); // Or equivalent route
            }
        };

        // Confirm
        const btnConfirm = overlay.querySelector('#btn-confirm');
        const btnText = btnConfirm.querySelector('#btn-text');
        const spinner = btnConfirm.querySelector('#spinner');

        btnConfirm.onclick = async () => {
            // UI Feedback
            btnConfirm.disabled = true;
            spinner.classList.remove('hidden');

            // Progressive Feedback Messages
            const messages = ["Connecting...", "Verifying...", "Finalizing..."];
            let msgIndex = 0;
            btnText.innerText = messages[0];

            const interval = setInterval(() => {
                msgIndex = (msgIndex + 1) % messages.length;
                // Only update if still processing (button disabled)
                if (btnConfirm.disabled && msgIndex < messages.length) {
                    btnText.innerText = messages[msgIndex];
                }
            }, 800); // Change message every 800ms

            try {
                // UI Repaint Breath
                await new Promise(r => setTimeout(r, 50));

                // Call external handler
                await this.onConfirm(this.selectedOption);

                clearInterval(interval);
                btnText.innerText = "Success!";
                spinner.classList.add('hidden');

                // Detailed Success visual (Checkmark) could be added here
                await new Promise(r => setTimeout(r, 500)); // Show Success briefly
                this.destroy();

            } catch (e) {
                console.error(e);
                clearInterval(interval);
                alert("Transaction Failed: " + e.message);

                // Reset UI
                btnConfirm.disabled = false;
                spinner.classList.add('hidden');
                btnText.innerText = "Confirm";
            }
        };
    }

    async initBalanceCheck() {
        if (!window.user) return; // Should allow modal for login prompt? Assuming guarded beforehand.

        try {
            const { getDoc, doc, getFirestore } = await import("firebase/firestore");
            const { app } = await import("../../firebase-config.js");
            const db = getFirestore(app);

            // Fresh Fetch (Trusted)
            const snap = await getDoc(doc(db, "users", window.user.uid));
            if (snap.exists()) {
                const d = snap.data();
                this.userPoints = d.activityPoints || 0;
                this.userStars = d.starPoints || 0;
                this.userLegacy = d.points || 0; // Legacy Points (Mixed)

                // Update Balance UI
                this.dom.querySelector('#user-points').innerText = `${this.userPoints} P`;
                this.dom.querySelector('#user-stars').innerText = `${this.userStars} Stars`;

                this.checkAffordability();
            }
        } catch (e) {
            console.warn("Balance check fail", e);
        }
    }

    checkAffordability() {
        if (this.userPoints === undefined) return; // Not loaded yet

        const btn = this.dom.querySelector('#btn-confirm');
        const glow = this.dom.querySelector('#btn-glow');

        let cost = (this.selectedOption === 'rent') ? this.rentPrice : this.ownPrice;
        let canAfford = false;

        // Logic: 
        // Rent (Usually Points or Stars) -> Prioritize Points? 
        // Own (Usually Stars Only) -> But our System allows mixing.

        // PointService.usePoints Logic:
        // tries Activity Points first, then Star Points.
        // So total liquid assets = Activity + Stars.
        // (Legacy 'points' is deprecated visual ref, we trust activity+star)

        const totalLiquid = this.userPoints + this.userStars;

        if (totalLiquid >= cost) {
            canAfford = true;
        }

        if (canAfford) {
            btn.disabled = false;
            btn.classList.remove('cursor-not-allowed', 'bg-white/10');
            btn.classList.add('shadow-lg', 'shadow-fuchsia-500/20'); // Base shadow
            glow.classList.remove('opacity-0');
            glow.classList.add('opacity-100');
        } else {
            btn.disabled = true;
            btn.classList.add('cursor-not-allowed', 'bg-white/10');
            btn.classList.remove('shadow-lg');
            glow.classList.remove('opacity-100');
            glow.classList.add('opacity-0');
            btn.querySelector('#btn-text').innerText = "Insufficient Funds";
        }
    }

    destroy() {
        const overlay = this.dom;
        if (overlay) {
            overlay.style.opacity = "0";
            if (overlay.firstChild) overlay.firstChild.style.transform = "scale(0.95)";

            setTimeout(() => {
                overlay.remove();
            }, 300);
            this.dom = null;
        }
    }
}
